import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useChatContext } from 'stream-chat-react';
import { z } from 'zod';

import { DuoChat } from '@/features/chat/components/duoChat';
import { ChatNotAvailable } from '@/features/chat/components/duoChat/chatNotAvailable';
import {
  getDuoChatOptions,
  useGetDuoChat,
} from '@/features/chat/hooks/useGetDuoChat';
import { Spinner } from '@/shared/components/ui/spinner';

const ParamsSchema = z.object({
  duoId: z.string(),
});

export const Route = createFileRoute(
  '/_protected/_app/_mainLayout/duo-chats/$duoId',
)({
  component: RouteComponent,
  errorComponent: ErrorComponent,
  params: {
    parse: params => ParamsSchema.parse(params),
  },
  loader: async ({ context, params: { duoId } }) => {
    await context.queryClient.ensureQueryData(getDuoChatOptions({ duoId }));
  },
  head: () => ({
    meta: [{ title: 'Duo Chat' }],
  }),
});

function ErrorComponent() {
  const navigate = useNavigate();

  const handleBackClick = () => {
    void navigate({ to: '/duo-chats' });
  };

  return <ChatNotAvailable onBackClick={handleBackClick} />;
}

function RouteComponent() {
  const { duoId } = Route.useParams();
  const { data: streamChannel } = useGetDuoChat({
    duoId,
    options: {
      select: data => data.getstreamChannel,
    },
  });

  const { client, channel, setActiveChannel } = useChatContext();

  useEffect(() => {
    if (streamChannel?.id) {
      setActiveChannel(client.channel(streamChannel.type, streamChannel.id));
    }
  }, [setActiveChannel, client, streamChannel?.id, streamChannel?.type]);

  // Ensure channel is:
  // - watched
  // - shown in channel list
  // - marked as read (to fix unread state issues)
  useEffect(() => {
    if (!channel) return;

    void (async () => {
      const { hidden } = await channel.watch();
      await channel.markRead();
      if (hidden) {
        await channel.show();
      }
    })();
  }, [channel]);

  const navigate = Route.useNavigate();
  const handleBackClick = () => {
    void navigate({ to: '/duo-chats' });
  };

  if (!streamChannel) {
    return (
      <div className="bg-chat-background flex size-full grow items-center justify-center">
        <Spinner className="size-10" />
      </div>
    );
  }

  return <DuoChat partner={{ userId: duoId }} onBackClick={handleBackClick} />;
}
